import { Dispatch, SetStateAction } from "react";
import { TFunction } from "react-i18next";
import { parseDateToDefaultString } from "go-core/components/Formatters/FormatDate";
import { FILTER_SEPARATOR, NEW_WAY_TO_ENCODING_FILTER_SIGN } from "go-list/core/components/Filter/services";
import { DashboardAlertApi } from "../../../services/Api/Organization/types";
import { DashboardAlert } from "../components/Alerts/DashboardAlerts";

export const getParsedAlerts = (
	setAlerts: Dispatch<SetStateAction<DashboardAlert[]>>,
	t: TFunction,
	organizationId?: number,
	dashboardAlerts?: DashboardAlertApi
) => {
	const newAlerts: DashboardAlert[] = [];
	if (dashboardAlerts) {
		const {
			apps_with_error,
			count_of_outdated_terminals,
			count_of_missing_ereceipts,
			count_of_not_sent_ereceipts,
			count_of_error_fiscalizations,
			count_of_duplicate_fiscalizations,
			count_of_not_sent_requests,
			count_of_not_connected_go_order_app_links,
			count_of_outdated_opened_orders,
			danger_pos_reports,
			warning_pos_reports,
			settings,
		} = dashboardAlerts;
		const alertTime = settings.REPORT_POS_SETTLEMENT_ALERT_TIME;

		apps_with_error?.forEach((app) => {
			app.errors
				.filter((err) => Object.keys(err).length > 0)
				.forEach((err) => {
					const dangerItem = {
						status: "DANGER",
						title: `${app.name}${err.code ? ` - ${t(`constraints.${err.code}`)}` : ""}`,
						link: app.id ? `/${organizationId}/apps/active/${app.id}` : `/${organizationId}/apps/all`,
					};
					newAlerts.push(dangerItem);
				});

			app.dangers
				.filter((err) => Object.keys(err).length > 0)
				.forEach((err) => {
					const warningItem = {
						status: "WARNING",
						title: `${app.name}${err.code ? ` - ${t(`constraints.${err.code}`)}` : ""}`,
						link: app.id ? `/${organizationId}/apps/active/${app.id}` : `/${organizationId}/apps/all`,
					};
					newAlerts.push(warningItem);
				});
		});

		if (count_of_outdated_terminals > 0) {
			const outdatedTerminalsAlert = {
				status: "WARNING",
				title: `${t("modules.dashboard.field.count_of_outdated_terminals.title", {
					amount: count_of_outdated_terminals,
				})}`,
				link: `/${organizationId}/settings/terminals?f=${btoa(
					unescape(encodeURIComponent(`${NEW_WAY_TO_ENCODING_FILTER_SIGN}up_to_date|false`))
				)}`,
			};
			newAlerts.push(outdatedTerminalsAlert);
		}
		if (count_of_outdated_opened_orders >= 100) {
			const isDanger = count_of_outdated_opened_orders >= 200;
			const today = new Date();
			const oneDayAgo = new Date();
			oneDayAgo.setDate(today.getDate() - 1);
			const outdatedOpenedOrdersAlert = {
				status: isDanger ? "DANGER" : "WARNING",
				title: t("modules.dashboard.field.count_of_outdated_opened_orders.title", {
					amount: count_of_outdated_opened_orders,
					time: alertTime,
				}),
				link: `/${organizationId}/sales/orders?f=${btoa(
					unescape(
						encodeURIComponent(
							`${NEW_WAY_TO_ENCODING_FILTER_SIGN}created_at|lte=${parseDateToDefaultString(
								oneDayAgo
							)}${FILTER_SEPARATOR}status|e=OPENED`
						)
					)
				)}`,
			};
			newAlerts.push(outdatedOpenedOrdersAlert);
		}

		if (count_of_not_sent_requests > 0) {
			const notSentRequestsAlert = {
				status: "DANGER",
				title: `${t("modules.dashboard.field.count_of_not_sent_requests.title", {
					amount: count_of_not_sent_requests,
				})}`,
				link: `/${organizationId}/logs/request_items?f=${btoa(
					unescape(encodeURIComponent(`${NEW_WAY_TO_ENCODING_FILTER_SIGN}status|e=ERROR`))
				)}`,
			};
			newAlerts.push(notSentRequestsAlert);
		}

		if (count_of_missing_ereceipts > 0) {
			const countOfMissingEordersAlert = {
				status: "DANGER",
				title: `${t("modules.dashboard.field.count_of_missing_ereceipts.title", {
					amount: count_of_missing_ereceipts,
				})}`,
				link: `/${organizationId}/sales/orders?f=${btoa(
					unescape(encodeURIComponent(`${NEW_WAY_TO_ENCODING_FILTER_SIGN}ereceipt|e=MISSING`))
				)}`,
			};
			newAlerts.push(countOfMissingEordersAlert);
		}

		if (count_of_not_connected_go_order_app_links > 0) {
			const countOfNotConnectedGoOrderAppLinks = {
				status: "WARNING",
				title: `${t("modules.dashboard.field.count_of_not_connected_go_order_app_links.title", {
					amount: count_of_not_connected_go_order_app_links,
				})}`,
				link: `/${organizationId}/apps/to_link`,
			};
			newAlerts.push(countOfNotConnectedGoOrderAppLinks);
		}

		if (count_of_not_sent_ereceipts > 0) {
			const countOfNotSentEordersAlert = {
				status: "DANGER",
				title: `${t("modules.dashboard.field.count_of_not_sent_ereceipts.title", {
					amount: count_of_not_sent_ereceipts,
				})}`,
				link: `/${organizationId}/sales/orders?f=${btoa(
					unescape(encodeURIComponent(`${NEW_WAY_TO_ENCODING_FILTER_SIGN}ereceipt|e=NOT_SENT`))
				)}`,
			};
			newAlerts.push(countOfNotSentEordersAlert);
		}

		if (count_of_error_fiscalizations > 0) {
			const countOfFiscalizationsErrors = {
				status: "DANGER",
				title: `${t("modules.dashboard.field.count_of_error_fiscalizations.title", {
					amount: count_of_error_fiscalizations,
				})}`,
				link: `/${organizationId}/sales/orders?f=${btoa(
					unescape(encodeURIComponent(`${NEW_WAY_TO_ENCODING_FILTER_SIGN}fiscalization_status|e=ERROR`))
				)}`,
			};
			newAlerts.push(countOfFiscalizationsErrors);
		}

		if (count_of_duplicate_fiscalizations > 0) {
			const countOfFiscalizationsDuplications = {
				status: "DANGER",
				title: `${t("modules.dashboard.field.count_of_duplicate_fiscalizations.title", {
					amount: count_of_duplicate_fiscalizations,
				})}`,
				link: `/${organizationId}/sales/orders?f=${btoa(
					unescape(encodeURIComponent(`${NEW_WAY_TO_ENCODING_FILTER_SIGN}ereceipt|e=DUPLICATE`))
				)}`,
			};
			newAlerts.push(countOfFiscalizationsDuplications);
		}

		danger_pos_reports?.forEach((report) => {
			const reportType = t(`enums.pos_reports.type.${report.type}`);
			const item = {
				status: "DANGER",
				title: t("modules.dashboard.field.danger_pos_reports.title", {
					type: reportType,
					id: report.id,
					time: alertTime,
				}),
				link: `/${organizationId}/sales/pos_reports/${report.id}`,
			};
			newAlerts.push(item);
		});

		warning_pos_reports?.forEach((report) => {
			const reportType = t(`enums.pos_reports.type.${report.type}`);
			const item = {
				status: "WARNING",
				title: t("modules.dashboard.field.warning_pos_reports.title", {
					type: reportType,
					id: report.id,
					time: alertTime,
				}),
				link: `/${organizationId}/sales/pos_reports/${report.id}`,
			};
			newAlerts.push(item);
		});
	}
	setAlerts([...newAlerts]);
	return newAlerts;
};
